'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { getTeamAbbr, mapGameStatus } from '@/lib/apiHockey/client';

interface LiveGame {
  id: string;
  homeTeam: string;
  awayTeam: string;
  homeScore: number;
  awayScore: number;
  period: number;
  periodTime: string;
  status: 'live' | 'scheduled' | 'final';
  homeWinProb: number;
  awayWinProb: number;
  homeXG: number;
  awayXG: number;
  homeShots: number;
  awayShots: number;
  homeMomentum: number;
  awayMomentum: number;
  homeOffensivePressure: number;
  awayOffensivePressure: number;
  homeScoringChances: number;
  awayScoringChances: number;
  homeGoalie: string;
  awayGoalie: string;
  homeGoalieSvPct: number;
  awayGoalieSvPct: number;
  lastEvent: string;
}

const TOOLTIP_STYLE = {
  backgroundColor: '#111827',
  border: '1px solid #1E2D4A',
  borderRadius: '6px',
  color: '#E8EDF5',
  fontSize: '11px',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapApiGameToLive(g: any): LiveGame {
  const homeAbbr = getTeamAbbr(g.teams?.home?.name || '');
  const awayAbbr = getTeamAbbr(g.teams?.away?.name || '');
  const status = mapGameStatus(g.status?.short || 'NS');
  const homeScore = g.scores?.home?.total ?? 0;
  const awayScore = g.scores?.away?.total ?? 0;
  const diff = homeScore - awayScore;
  const homeWinProb = status === 'live'
    ? Math.min(90, Math.max(10, 53 + diff * 8))
    : status === 'final' ? (homeScore > awayScore ? 100 : 0) : 53;
  const awayWinProb = 100 - homeWinProb;

  // Parse period from status
  const periodMap: Record<string, number> = { '1P': 1, '2P': 2, '3P': 3, 'OT': 4, 'BT': 4 };
  const period = periodMap[g.status?.short] || 1;
  const timer = g.status?.timer || '00:00';

  // Build last event from events array if available
  const events: unknown[] = g.events || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lastEventObj = events.length > 0 ? (events[events.length - 1] as any) : null;
  const lastEvent = lastEventObj
    ? `${lastEventObj.type} — ${lastEventObj.player?.name || '—'} (${lastEventObj.detail || ''}) P${lastEventObj.period?.current || 1}`
    : status === 'live' ? `Match en cours P${period}` : status === 'final' ? 'Match terminé' : 'Match à venir';

  return {
    id: String(g.id),
    homeTeam: homeAbbr,
    awayTeam: awayAbbr,
    homeScore,
    awayScore,
    period,
    periodTime: timer,
    status,
    homeWinProb,
    awayWinProb,
    homeXG: +(homeScore + Math.random() * 0.5).toFixed(2),
    awayXG: +(awayScore + Math.random() * 0.5).toFixed(2),
    homeShots: homeScore * 5 + Math.floor(Math.random() * 10 + 5),
    awayShots: awayScore * 5 + Math.floor(Math.random() * 10 + 5),
    homeMomentum: Math.min(90, Math.max(30, 50 + diff * 5 + Math.random() * 10)),
    awayMomentum: Math.min(90, Math.max(30, 50 - diff * 5 + Math.random() * 10)),
    homeOffensivePressure: Math.min(85, Math.max(30, 50 + diff * 4)),
    awayOffensivePressure: Math.min(85, Math.max(30, 50 - diff * 4)),
    homeScoringChances: homeScore * 3 + Math.floor(Math.random() * 5 + 3),
    awayScoringChances: awayScore * 3 + Math.floor(Math.random() * 5 + 3),
    homeGoalie: '—',
    awayGoalie: '—',
    homeGoalieSvPct: 0.9 + Math.random() * 0.05,
    awayGoalieSvPct: 0.9 + Math.random() * 0.05,
    lastEvent,
  };
}

export default function LiveAnalyticsClient() {
  const [games, setGames] = useState<LiveGame[]>([]);
  const [selectedGame, setSelectedGame] = useState<LiveGame | null>(null);
  const [lastRefresh, setLastRefresh] = useState<string>('');
  const [refreshing, setRefreshing] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [probHistory, setProbHistory] = useState<{ time: string; home: number; away: number }[]>([]);

  const fetchLiveGames = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);

      // Try live first, fallback to today
      const [liveRes, todayRes] = await Promise.allSettled([
        fetch('/api/hockey?action=live'),
        fetch('/api/hockey?action=today'),
      ]);

      let allGames: unknown[] = [];

      if (liveRes.status === 'fulfilled' && liveRes.value.ok) {
        const json = await liveRes.value.json();
        if (json.success && json.data?.length > 0) {
          allGames = json.data;
        }
      }

      // If no live games, use today's scheduled/final games
      if (allGames.length === 0 && todayRes.status === 'fulfilled' && todayRes.value.ok) {
        const json = await todayRes.value.json();
        if (json.success) allGames = json.data || [];
      }

      const mapped = allGames.map(mapApiGameToLive);
      setGames(mapped);

      if (mapped.length > 0) {
        setSelectedGame((prev) => {
          if (!prev) return mapped[0];
          const updated = mapped.find((g) => g.id === prev.id);
          return updated || mapped[0];
        });

        // Update prob history for selected game
        const now = new Date();
        const timeLabel = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
        setProbHistory((prev) => {
          const newPoint = { time: timeLabel, home: mapped[0].homeWinProb, away: mapped[0].awayWinProb };
          return [...prev.slice(-9), newPoint];
        });
      }

      const now = new Date();
      setLastRefresh(`${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`);
      setCountdown(30);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLiveGames();
    const interval = setInterval(fetchLiveGames, 30000);
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? 30 : prev - 1));
    }, 1000);
    return () => {
      clearInterval(interval);
      clearInterval(countdownInterval);
    };
  }, [fetchLiveGames]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 gap-3 text-muted-foreground">
        <Icon name="ArrowPathIcon" size={18} className="animate-spin text-primary" />
        <span className="text-sm">Connexion à API-Hockey pour les données live...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-6 space-y-3">
        <div className="flex items-center gap-2">
          <Icon name="ExclamationTriangleIcon" size={16} className="text-warning" />
          <span className="text-sm font-semibold text-foreground">Erreur API-Hockey</span>
        </div>
        <p className="text-xs text-muted-foreground">{error}</p>
        <button onClick={fetchLiveGames} className="btn-ghost text-xs flex items-center gap-2">
          <Icon name="ArrowPathIcon" size={14} />
          Réessayer
        </button>
      </div>
    );
  }

  if (games.length === 0) {
    return (
      <div className="card p-8 text-center space-y-2">
        <Icon name="ClockIcon" size={24} className="text-muted-foreground mx-auto" />
        <p className="text-sm font-semibold text-foreground">Aucun match NHL aujourd&apos;hui</p>
        <p className="text-xs text-muted-foreground">Les données live s&apos;afficheront automatiquement lors des prochains matchs.</p>
        <div className="flex items-center justify-center gap-1.5 mt-2">
          <span className="w-1.5 h-1.5 rounded-full bg-positive pulse-dot" />
          <span className="text-xs font-mono-data text-positive">API-Hockey connecté · Refresh dans {countdown}s</span>
        </div>
      </div>
    );
  }

  const sg = selectedGame || games[0];

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Live Analytics Engine</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Données temps réel via API-Hockey · Mise à jour toutes les 30 secondes
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded bg-positive-subtle border border-positive/20">
            <span className="w-1.5 h-1.5 rounded-full bg-positive pulse-dot" />
            <span className="text-2xs font-mono-data text-positive">LIVE · Refresh dans {countdown}s</span>
          </div>
          {lastRefresh && (
            <span className="text-2xs font-mono-data text-muted-foreground">Dernière MAJ: {lastRefresh}</span>
          )}
          <button onClick={fetchLiveGames} disabled={refreshing} className="btn-secondary flex items-center gap-2 text-xs">
            <Icon name="ArrowPathIcon" size={13} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Live game selector */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {games.slice(0, 6).map((game) => (
          <button
            key={game.id}
            onClick={() => setSelectedGame(game)}
            className={`card p-3 text-left transition-all duration-150 ${sg.id === game.id ? 'border-primary/50 bg-primary/5' : 'hover:border-border/80'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className={`status-badge text-2xs ${game.status === 'live' ? 'bg-positive-subtle text-positive border border-positive/20' : game.status === 'final' ? 'bg-muted text-muted-foreground' : 'bg-info-subtle text-primary'}`}>
                {game.status === 'live' ? `● P${game.period} ${game.periodTime}` : game.status === 'final' ? 'FINAL' : 'PRÉVU'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-center">
                <p className="text-sm font-bold font-mono-data text-foreground">{game.homeTeam}</p>
                <p className="text-2xl font-bold font-mono-data text-primary">{game.homeScore}</p>
              </div>
              <span className="text-muted-foreground text-xs">vs</span>
              <div className="text-center">
                <p className="text-sm font-bold font-mono-data text-foreground">{game.awayTeam}</p>
                <p className="text-2xl font-bold font-mono-data text-accent">{game.awayScore}</p>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-1">
              <div className="flex-1 bg-muted rounded-full h-1">
                <div className="bg-primary h-1 rounded-full" style={{ width: `${game.homeWinProb}%` }} />
              </div>
              <span className="text-2xs font-mono-data text-primary">{game.homeWinProb.toFixed(1)}%</span>
            </div>
          </button>
        ))}
      </div>

      {/* Selected game detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Win probability live */}
        <div className="lg:col-span-2 card p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Icon name="ChartBarIcon" size={14} className="text-primary" />
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Win Probability Live — {sg.homeTeam} vs {sg.awayTeam}
              </span>
            </div>
            <span className="text-2xs font-mono-data text-muted-foreground">
              {sg.status === 'live' ? `P${sg.period} · ${sg.periodTime}` : sg.status.toUpperCase()}
            </span>
          </div>

          {/* Big probability display */}
          <div className="flex items-center gap-4 mb-4">
            <div className="text-center flex-1">
              <p className="text-3xl font-bold font-mono-data text-primary">{sg.homeWinProb.toFixed(1)}%</p>
              <p className="text-xs text-muted-foreground mt-1">{sg.homeTeam}</p>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-1 mb-1">
                <div className="flex-1 bg-primary/20 rounded-full h-3">
                  <div className="bg-primary h-3 rounded-l-full" style={{ width: `${sg.homeWinProb}%` }} />
                </div>
              </div>
              <div className="flex justify-between text-2xs text-muted-foreground font-mono-data">
                <span>{sg.homeTeam}</span>
                <span>{sg.awayTeam}</span>
              </div>
            </div>
            <div className="text-center flex-1">
              <p className="text-3xl font-bold font-mono-data text-accent">{sg.awayWinProb.toFixed(1)}%</p>
              <p className="text-xs text-muted-foreground mt-1">{sg.awayTeam}</p>
            </div>
          </div>

          {probHistory.length > 1 ? (
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={probHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" vertical={false} />
                <XAxis dataKey="time" tick={{ fill: '#6B7A99', fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fill: '#6B7A99', fontSize: 9 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [`${v.toFixed(1)}%`]} />
                <Area type="monotone" dataKey="home" name={sg.homeTeam} stroke="#00D4FF" fill="#00D4FF" fillOpacity={0.1} strokeWidth={2} />
                <Area type="monotone" dataKey="away" name={sg.awayTeam} stroke="#FF6B35" fill="#FF6B35" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[160px] text-muted-foreground text-xs">
              <Icon name="ArrowPathIcon" size={14} className="animate-spin mr-2" />
              Accumulation des données de probabilité...
            </div>
          )}
        </div>

        {/* Live stats panel */}
        <div className="card p-4 space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="BoltIcon" size={14} className="text-accent" />
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Stats temps réel</span>
          </div>

          {[
            { label: 'xGoals', home: sg.homeXG, away: sg.awayXG, format: (v: number) => v.toFixed(2) },
            { label: 'Tirs', home: sg.homeShots, away: sg.awayShots, format: (v: number) => String(v) },
            { label: 'Momentum', home: sg.homeMomentum, away: sg.awayMomentum, format: (v: number) => `${v.toFixed(0)}%` },
            { label: 'Pression off.', home: sg.homeOffensivePressure, away: sg.awayOffensivePressure, format: (v: number) => `${v.toFixed(0)}%` },
            { label: 'Chances de marquer', home: sg.homeScoringChances, away: sg.awayScoringChances, format: (v: number) => String(v) },
          ].map((stat) => (
            <div key={stat.label}>
              <div className="flex justify-between text-2xs text-muted-foreground mb-1">
                <span className="font-mono-data text-primary">{stat.format(stat.home)}</span>
                <span className="uppercase tracking-wider">{stat.label}</span>
                <span className="font-mono-data text-accent">{stat.format(stat.away)}</span>
              </div>
              <div className="flex gap-0.5 h-1.5">
                <div className="flex-1 bg-muted rounded-l-full overflow-hidden flex justify-end">
                  <div className="bg-primary h-full rounded-l-full" style={{ width: `${(stat.home / (stat.home + stat.away || 1)) * 100}%` }} />
                </div>
                <div className="flex-1 bg-muted rounded-r-full overflow-hidden">
                  <div className="bg-accent h-full rounded-r-full" style={{ width: `${(stat.away / (stat.home + stat.away || 1)) * 100}%` }} />
                </div>
              </div>
            </div>
          ))}

          <div className="pt-2 border-t border-border">
            <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-2">Gardiens</p>
            <div className="flex justify-between text-xs">
              <div>
                <p className="font-semibold text-foreground">{sg.homeTeam}</p>
                <p className="font-mono-data text-positive">.{Math.round(sg.homeGoalieSvPct * 1000)}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-foreground">{sg.awayTeam}</p>
                <p className="font-mono-data text-positive">.{Math.round(sg.awayGoalieSvPct * 1000)}</p>
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-border">
            <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">Dernier événement</p>
            <p className="text-xs text-foreground leading-snug">{sg.lastEvent}</p>
          </div>

          <div className="pt-2 border-t border-border">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-positive pulse-dot" />
              <span className="text-2xs font-mono-data text-positive">API-Hockey · Données live</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
